import type { AdminPayoutListItemDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { PayoutsFilterBar } from './_components/PayoutsFilterBar';
import { buildPayoutColumns } from './_lib/payoutColumns';
import { hasActiveFilters, payoutFiltersSchema } from './_lib/payoutFilters';
import { PayoutDetailDrawer } from './PayoutDetailDrawer';
import { useAdminPayoutsList } from '../../api/admin-payouts';
import { RequirePermission } from '../../auth';
import { AdminDataGrid } from '../../components/data-grid';
import { useCursorPagination } from '../../hooks/useCursorPagination';
import { useUrlFilters } from '../../hooks/useUrlFilters';

/**
 * `/payouts` — admin payouts triage queue (AUT-128 / ADMIN-16).
 *
 * Gated on `process_payouts` (the controller-level permission). The
 * inspector-name column renders via `<RedactedField perm="view_pii">`,
 * which agrees with the server's `piiRevealed` flag (payouts are
 * inspector-side — no customer identity anywhere).
 *
 * Filters + pagination are URL-state-driven via `useUrlFilters`; cursor
 * pagination bridges the cursor backend to the page-based DataGrid via
 * `useCursorPagination`. No default status filter — the queue opens
 * unfiltered. Row click opens the 720px detail drawer.
 *
 * Selection holds only the row ID (`selectedId`); the row itself is
 * DERIVED from the freshest `items` slice on every render. This matters
 * for the eligibility flags: after a rejected retry / mark-manual the
 * action hook invalidates and the list refetches with fresh rows — a
 * frozen row snapshot would keep showing a now-invalid action button and
 * let the operator re-click straight into the same 422 / 409. Deriving
 * re-syncs `retryEligible` / `markManualEligible` (the detail DTO does
 * not carry them, so the drawer still sources them from the list row).
 * Fail-closed: if the selected row has left the fresh slice (filter /
 * page moved it out) the derived row is `null` and both eligibility
 * flags pass as `false` — never surface an action we can't confirm from
 * fresh data. The drawer stays open-by-id (its detail query is keyed by
 * id, independent of the list filter), so the operator keeps the detail.
 *
 * Clones `ReportsListPage` shape — same primitive stack, same observer
 * wrap, same filter-reset-on-change effect.
 */
const PayoutsListPageInner = (): ReactElement => {
  const { t } = useTranslation();
  const { filters, setFilters, reset } = useUrlFilters(payoutFiltersSchema);
  const pager = useCursorPagination();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Any filter change resets pagination to the first page (the cursor
  // space is only valid for the filter set that produced it).
  const filterKey = `${(filters.status ?? []).join(',')}|${filters.inspectorId ?? ''}|${
    filters.dateFrom ?? ''
  }|${filters.dateTo ?? ''}`;
  useEffect(() => {
    pager.reset();
    // `pager.reset` is stable (useCallback); re-run only on filter change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const query = useAdminPayoutsList({
    ...(filters.status && filters.status.length > 0 ? { status: filters.status } : {}),
    ...(filters.inspectorId ? { inspectorId: filters.inspectorId } : {}),
    ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
    ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
    cursor: pager.cursor,
    limit: pager.pageSize,
  });

  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const nextCursor = query.data?.nextCursor ?? null;

  // Derive the selected row from the FRESHEST slice — never a frozen
  // snapshot. `null` when the row has left the current slice (fail-closed
  // on eligibility downstream).
  const selected = useMemo(
    () => items.find((r) => r.id === selectedId) ?? null,
    [items, selectedId],
  );

  // Feed the latest nextCursor into the pager once each fetch settles.
  useEffect(() => {
    if (query.isSuccess) pager.onResult(nextCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, query.dataUpdatedAt, nextCursor]);

  const columns = useMemo(() => buildPayoutColumns(t), [t]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Typography variant="h1" sx={{ fontSize: 22, fontWeight: 700 }}>
          {t('payouts.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('payouts.subtitle')}
        </Typography>
      </Box>

      <PayoutsFilterBar
        filters={filters}
        setFilters={setFilters}
        canClear={hasActiveFilters(filters)}
        onClear={reset}
      />

      <AdminDataGrid<AdminPayoutListItemDto>
        rows={items}
        columns={columns}
        rowCount={pager.rowCount(items.length)}
        loading={query.isLoading || query.isFetching}
        error={query.error}
        paginationModel={pager.paginationModel}
        onPaginationModelChange={pager.onPaginationModelChange}
        sortModel={[]}
        onSortModelChange={() => undefined}
        onRowClick={(row) => setSelectedId(row.id)}
        emptyState={
          <Typography variant="body2" color="text.secondary">
            {t('payouts.empty')}
          </Typography>
        }
      />

      <PayoutDetailDrawer
        payoutId={selectedId}
        retryEligible={selected?.retryEligible ?? false}
        markManualEligible={selected?.markManualEligible ?? false}
        onClose={() => setSelectedId(null)}
      />
    </Box>
  );
};

const Observed = observer(PayoutsListPageInner);

/** Whole-page gate: no `process_payouts` → the default 403 panel. */
export const PayoutsListPage = (): ReactElement => (
  <RequirePermission perm="process_payouts">
    <Observed />
  </RequirePermission>
);
